import React from "react"
import { type ApolloError } from "@apollo/client"
import { isNetworkRequestInFlight } from "@apollo/client/core/networkStatus"
import {
  DirectionFromOrigin,
  type EntityParty,
  ProductArea,
  SummaryDimension,
  type TransactionSummary,
  useSummarizeTransactionsByTimeByPartyQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import objectHelper from "@digits-shared/helpers/objectHelper"
import { molecule, use } from "bunshi"
import { useMolecule } from "bunshi/react"
import { atom, useAtomValue, useSetAtom } from "jotai"
import { LegalEntityIdScope } from "src/frontend/components/LegalEntityIdScope"
import { usePartyRoles } from "src/frontend/components/OS/Applications/VendorDirectory/hooks/usePartyRoles"
import { useVendorCustomerCustomization } from "src/frontend/components/OS/Applications/VendorDirectory/hooks/useVendorCustomerCustomization"
import { vendorDataMolecule } from "src/frontend/components/OS/Applications/VendorDirectory/moleculeHydrators/vendorDataHydrator"
import { VendorDirectoryScope } from "src/frontend/components/OS/Applications/VendorDirectory/VendorScopeProvider"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

// Data to support new vendors on the filter accordion and in the dashboard widget
export const newVendorsMolecule = molecule(() => {
  // Vendor directory scope does not provide a value that changes, so it just specifies the scope boundary for vendor directory data
  use(VendorDirectoryScope)
  use(LegalEntityIdScope)

  const timeByPartyAtom = atom<TimeyByPartySummary[] | undefined>(undefined)
  const loadingAtom = atom(false)
  const errorAtom = atom<ApolloError | undefined>(undefined)

  const newPartiesAtom = atom<Set<EntityParty>>((get) => {
    const partiesByTime = get(timeByPartyAtom)
    /*
      No Custom Range
        If the user has not selected a custom range there will only be one period (interval count 1)
        and any party with a prior transaction count of 0 will be considered new.
      Custom Range
        If the user has selected a custom range (interval count > 1), we select the oldest period from the list
        and only consider a party new if it has no prior transactions as compared to the oldest period.
        The way the dimensional summary query works prevents us from simply querying for the earliest period with an interval count of 1
        and checking if the party has prior transactions because the query will only return parties that had transactions in that earliest period
        even if the party ids are passed in.
        e.g. If the range is August and September and a party has transactions in september but not august
        and we query for August, interval count of 1, and include our september party Id it will not be returned in the query results
    */
    const oldestPeriodIndex = partiesByTime ? partiesByTime.length - 1 : 0

    return new Set(
      partiesByTime?.[oldestPeriodIndex]?.by.party
        .filter((party) =>
          party?.summary.total.prior?.count ? party?.summary.total.prior.count === 0 : true
        )
        .sort((a, b) => objectHelper.compareObjectNames(a.partyObject, b.partyObject))
        .map((party) => party.partyObject) ?? []
    )
  })

  const newPartyIdsAtom = atom(
    (get) => Array.from(get(newPartiesAtom)).map((party) => party.id) ?? []
  )
  const countAtom = atom((get) => get(newPartiesAtom).size)

  return {
    loadingAtom,
    errorAtom,
    timeByPartyAtom,
    newPartiesAtom,
    newPartyIdsAtom,
    countAtom,
  }
})

/*
  INTERFACES
*/

interface TimeyByPartySummary {
  summary: TransactionSummary
  by: {
    party: PartySummary[]
  }
}

interface PartySummary {
  summary: TransactionSummary
  partyObject: EntityParty
}

/*
  HYDRATORS
*/

// useHydrateNewVendorsMolecule hydrates the vendor data molecule with the results of the summarize transactions by time by party query
export const useHydrateNewVendorsMolecule = () => {
  const intervalOrigin = useIntervalOrigin()
  const viewKey = useViewVersion()
  const partyRoles = usePartyRoles()
  const { defaultCategoryType } = useVendorCustomerCustomization()

  const { partyIdsAtom, loadingAtom: vendorSummaryLoadingAtom } = useMolecule(vendorDataMolecule)
  const partyIds = useAtomValue(partyIdsAtom)
  const vendorSummaryLoading = useAtomValue(vendorSummaryLoadingAtom)

  const { errorAtom, loadingAtom, timeByPartyAtom } = useMolecule(newVendorsMolecule)
  const setError = useSetAtom(errorAtom)
  const setLoading = useSetAtom(loadingAtom)
  const setTimeByParty = useSetAtom(timeByPartyAtom)

  const { data, error, networkStatus } = useSummarizeTransactionsByTimeByPartyQuery({
    variables: {
      filter: {
        viewKey,
        productArea: ProductArea.None,
        partyRoles,
        partyIds,
      },
      direction: DirectionFromOrigin.Past,
      asPermanent: true,
      masks: [SummaryDimension.Time, SummaryDimension.Party],
      origin: {
        ...intervalOrigin,
        intervalCount: intervalOrigin?.intervalCount || 1,
      },
      defaultCategoryType,
    },
    notifyOnNetworkStatusChange: true,
  })

  React.useEffect(() => {
    if (!isNetworkRequestInFlight(networkStatus) && data) {
      setTimeByParty(data?.dimensionalTransactionSummary?.time)
    }
  }, [data, setTimeByParty, networkStatus])

  React.useEffect(() => {
    if (error) {
      setError(error)
    }
  }, [error, setError])

  React.useEffect(() => {
    setLoading(isNetworkRequestInFlight(networkStatus) || vendorSummaryLoading)
  }, [networkStatus, setLoading, vendorSummaryLoading])
}
